/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import { types, getEnv, destroy } from 'mobx-state-tree';
import BadgeTooltipModel from 'features/badges/BadgeTooltipModel';
import NaturalLanguageTextModel from 'components/nlt/NaturalLanguageTextModel';
import ImageCaptureModel from 'components/image-capture/ImageCaptureModel';
import WidgetAPI from 'api/WidgetAPI';
import { fromPromise } from 'mobx-utils';

export const WidgetLayoutModelExt = types
	.model({})
	.volatile(() => ({
		renderId: 0,
		badgesTooltipModel: null,
		nlt: null,
		imageCapture: null,
		badgesFeature: null,
		readyPromise: null
	}))
	.views((self) => ({
		getBadgesTooltipModel() {
			return self.badgesTooltipModel;
		},
		getImageCaptureModel() {
			return self.imageCapture;
		},
		/**
		 * @param {slotId} slot id of the widget, -1 means title
		 * @return {object} promise with badge info
		 */
		getBadgeInfoWhenReady(slotId){
			return fromPromise(self.badgesFeature ? self.badgesFeature.getBadgeDataWhenReady(slotId):Promise.resolve([]));
		}
	}))
	.actions(self => ({
		afterCreate(){
			self.badgesTooltipModel = BadgeTooltipModel.create({
				isOpen: false,
				badgesInfo: {},
				position: {}
			});

			self.nlt = NaturalLanguageTextModel.create({
				id: self.id
			}, getEnv(self));

			self.imageCapture = ImageCaptureModel.create({
				id: self.id
			}, getEnv(self));

			const {
				dashboardApi
			} = getEnv(self);

			// Attach a listener to monitor the badge info change in the widget
			if (dashboardApi) {
				self.readyPromise = dashboardApi.getCanvasWhenReady()
					.then(canvas => canvas.getWidgetWhenReady(self.id))
					.then((widget) => {
						const widgetModel = self.boardModel.findWidgetById(self.id);
						if (widgetModel && self.imageCapture) {
							self.imageCapture.setModel_HACK(widgetModel);
						}
						if(widget.getFeature) {
							const badgesFeature = widget.getFeature('vis-badges');
							if (badgesFeature) {
								badgesFeature.onBadgeSelect(self.toggleBadgeTooltip);
								self.setBadgeFeature(badgesFeature);
							}
						}
					});
			}
		},
		beforeDestroy() {
			self.readyPromise = null;
			[
				self.badgesTooltipModel,
				self.nlt,
				self.imageCapture
			].forEach(model => {
				if (model) {
					destroy(model);
				}
			});
		},
		toggleBadgeTooltip(badgesInfo, position) {
			if (self.badgesTooltipModel.isTooltipOpen()) {
				self.badgesTooltipModel.closeTooltip();
			} else {
				self.badgesTooltipModel.openTooltip(position);
				self.badgesTooltipModel.setBadgesInfo(badgesInfo);
			}
		},
		forceRender_HACK() {
			self.renderId++;
		},
		setStaticWidget(value) {
			self.isStaticWidget = value;
		},
		setBadgeFeature(feature){
			self.badgesFeature = feature;
		},
		getAPI() {
			if(!self.api) {
				self.api = new WidgetAPI(self);
			}
			return self.api;
		}
	}))
	.named('WidgetLayoutModelExt')
	.props({
		type: types.optional(types.string, 'widget'),
		isStaticWidget: types.optional(types.boolean, false)
	});



// WEBPACK FOOTER //
// ./src/js/core/WidgetLayoutModelExt.js